import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const CarbonSection = () => {
  const entreprisesMissions = [
    "Identification de forêts pouvant accueillir des projets carbone",
    "Structuration de projets forestiers compatibles avec les standards reconnus",
    "Mise en relation avec les propriétaires forestiers",
    "Suivi et valorisation environnementale des projets",
  ];

  const proprietairesServices = [
    "L'identification du potentiel carbone de leurs parcelles",
    "La structuration de projets carbone",
    "La recherche d'entreprises partenaires pour financer ces projets",
  ];

  const financementsUsages = [
    "La reconstitution forestière",
    "L'amélioration des peuplements",
    "L'adaptation des forêts au changement climatique",
    "La gestion durable des massifs forestiers",
  ];

  return (
    <section
      id="carbone"
      data-testid="carbon-section"
      className="py-24 md:py-32 bg-forest text-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">
            Nouveau Service
          </p>
          <h2
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight text-white mb-6"
            data-testid="carbon-headline"
          >
            Carbone Forestier
          </h2>
          <div className="w-16 h-0.5 bg-gold mx-auto mb-8" />
          <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed max-w-3xl mx-auto">
            Les forêts jouent un rôle majeur dans la captation du CO₂ et la lutte contre le changement climatique.
            Sylva Invest développe et facilite des projets carbone forestiers en mettant en relation des entreprises
            souhaitant financer des projets environnementaux et des propriétaires forestiers disposant de parcelles à valoriser.
          </p>
        </motion.div>

        {/* Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Pour les Entreprises */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white/10 backdrop-blur-sm p-8 md:p-10"
            data-testid="carbon-entreprises"
          >
            <p className="text-xs uppercase tracking-[0.15em] text-gold font-sans font-semibold mb-3">
              Pour les Entreprises
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-6">
              Entreprises : financez des projets carbone forestiers
            </h3>
            <p className="font-sans text-white/80 leading-relaxed mb-6">
              Nous accompagnons les entreprises dans l'identification et le financement de projets carbone forestiers en France.
            </p>
            
            <p className="font-sans font-semibold text-gold mb-4">Nos missions :</p>
            <ul className="space-y-3 mb-6">
              {entreprisesMissions.map((mission, index) => (
                <li key={index} className="flex items-start text-white/80">
                  <ArrowRight className="w-4 h-4 text-gold mr-3 mt-1 flex-shrink-0" />
                  <span>{mission}</span>
                </li>
              ))}
            </ul>
            
            <div className="p-4 bg-gold/20 border-l-4 border-gold">
              <p className="font-sans text-white/90 text-sm">
                <strong className="text-gold">Objectif :</strong> permettre aux entreprises d'investir dans des projets carbone concrets, locaux et traçables.
              </p>
            </div>
          </motion.div>

          {/* Pour les Propriétaires */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-sm p-8 md:p-10"
            data-testid="carbon-proprietaires"
          >
            <p className="text-xs uppercase tracking-[0.15em] text-gold font-sans font-semibold mb-3">
              Pour les Propriétaires
            </p>
            <h3 className="font-serif text-2xl md:text-3xl text-white mb-6">
              Propriétaires forestiers : valorisez le carbone de votre forêt
            </h3>
            <p className="font-sans text-white/80 leading-relaxed mb-6">
              Certaines forêts disposent d'un potentiel de séquestration carbone important encore peu valorisé.
            </p>
            
            <p className="font-sans font-semibold text-gold mb-4">Sylva Invest accompagne les propriétaires forestiers dans :</p>
            <ul className="space-y-3 mb-6">
              {proprietairesServices.map((service, index) => (
                <li key={index} className="flex items-start text-white/80">
                  <ArrowRight className="w-4 h-4 text-gold mr-3 mt-1 flex-shrink-0" />
                  <span>{service}</span>
                </li>
              ))}
            </ul>
            
            <p className="font-sans font-semibold text-gold mb-4">Ces financements peuvent contribuer à :</p>
            <ul className="space-y-3">
              {financementsUsages.map((usage, index) => (
                <li key={index} className="flex items-start text-white/80">
                  <ArrowRight className="w-4 h-4 text-gold mr-3 mt-1 flex-shrink-0" />
                  <span>{usage}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <a
            href="#contact"
            className="btn-primary bg-gold text-forest hover:bg-white hover:text-forest inline-block"
            data-testid="carbon-cta"
          >
            Discuter d'un projet carbone
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default CarbonSection;
